import React from "react";
import { Sparkles, Code2, Mail } from "lucide-react";

export interface TemplateCardProps {
  id: string | number;
  icon?: "ai" | "custom" | "template";
  title: string;
  description: string;
  badges?: Array<{
    label: string;
    variant: "primary" | "secondary" | "success";
  }>;
  stats?: {
    users?: number;
    successRate?: number;
  };
  onClick?: () => void;
}

const getIconAndColors = (iconType?: string) => {
  switch (iconType) {
    case "ai":
      return {
        icon: (
          <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-purple-600" />
          </div>
        ),
        borderColor: "border-gray-300",
        bgColor: "bg-white",
      };
    case "custom":
      return {
        icon: (
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
            <Code2 className="w-8 h-8 text-blue-600" />
          </div>
        ),
        borderColor: "border-blue-500",
        bgColor: "bg-white",
      };
    case "template":
      return {
        icon: (
          <div className="w-full h-20 bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center rounded-t-lg mb-0">
            <Mail className="w-8 h-8 text-white" />
          </div>
        ),
        borderColor: "border-gray-300",
        bgColor: "bg-white",
      };
    default:
      return {
        icon: null,
        borderColor: "border-gray-300",
        bgColor: "bg-white",
      };
  }
};

const getBadgeStyles = (variant: string) => {
  const baseStyles = "text-xs font-medium px-2.5 py-1 rounded";
  switch (variant) {
    case "primary":
      return `${baseStyles} bg-blue-100 text-blue-600`;
    case "secondary":
      return `${baseStyles} bg-red-100 text-red-600`;
    case "success":
      return `${baseStyles} bg-green-100 text-green-600`;
    default:
      return baseStyles;
  }
};

export const TemplateCard: React.FC<TemplateCardProps> = ({
  id,
  icon,
  title,
  description,
  badges,
  stats,
  onClick,
}) => {
  const { icon: iconComponent, borderColor, bgColor } = getIconAndColors(icon);
  const isTemplateType = icon === "template";

  return (
    <div
      onClick={onClick}
      className={`rounded-lg cursor-pointer transition-all shadow-lg overflow-hidden ${
        isTemplateType ? "border-0" : `border-2 ${borderColor}`
      } ${bgColor}`}
    >
      {/* Icon Section - Full width for template type */}
      {iconComponent && (
        <div className={isTemplateType ? "" : "px-6 pt-4"}>{iconComponent}</div>
      )}

      {/* Content Section */}
      <div className={`${isTemplateType ? "px-6 py-3" : "px-6 pb-4"}`}>
        {/* Title */}
        <h3 className="text-base font-bold text-gray-900 mb-1">{title}</h3>

        {/* Description */}
        <p className="text-xs text-gray-600 mb-3">{description}</p>

        {/* Badges */}
        {badges && badges.length > 0 && (
          <div className="flex gap-2 mb-2">
            {badges.map((badge, idx) => (
              <span key={idx} className={getBadgeStyles(badge.variant)}>
                {badge.label}
              </span>
            ))}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="flex gap-3 text-xs text-gray-600 mb-2">
            {stats.users && (
              <span className="flex items-center gap-1">
                <span>👥</span>
                <span>{stats.users.toLocaleString()} users</span>
              </span>
            )}
            {stats.successRate && (
              <span className="flex items-center gap-1 text-green-600 font-medium">
                <span>✓</span>
                <span>{stats.successRate}% success</span>
              </span>
            )}
          </div>
        )}

        {/* Footer Action Text */}
        {icon === "ai" && (
          <div className="mt-4 flex justify-center gap-1 text-purple-600 text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            AI Powered
          </div>
        )}
{icon === "ai" && (
  <div className="mt-3 bg-blue-50 text-center py-1 px-2 rounded-md">
    <p className="text-[11px] font-semibold text-blue-600">
      Coming Soon
    </p>
  </div>
)}
        {icon === "custom" && (
          <div className="mt-4 flex justify-center gap-1 text-blue-600 text-sm font-medium">
            <Code2 className="w-4 h-4" />
            Full Control
          </div>
        )}
      </div>
    </div>
  );
};
